import * as Sentry from '@sentry/react'
import type {
  LoginResponse,
  RegisterResponse,
  UserPublic,
  TwitterAccountPublic,
  VoiceAnalysisResponse,
  VoiceAnalysisStatusResponse,
  VoiceProfileResponse,
  ContentGenerateResponse,
  GenerationJobStatusResponse,
  ContentType,
  ToneOverride,
  ContextType,
  QueueListResponse,
  QueueItemResponse,
  QueueItemUpdateRequest,
  QueueItemUpdateResponse,
  PostToTwitterResponse,
  PostToPlatformResponse,
  MediaUploadResponse,
  ScheduleContentResponse,
  CancelScheduleResponse,
  RemixResponse,
  AnalyticsListResponse,
  AnalyticsSummaryResponse,
  MetricsRefreshResponse,
  SettingsGetResponse,
  SettingsUpdateRequest,
  SettingsUpdateResponse,
  OnboardingProgressResponse,
  ApiErrorResponse,
  PasswordResetRequestResponse,
  PasswordResetVerifyResponse,
  PasswordResetCompleteResponse,
  PasswordChangeResponse,
  PlanTier,
  CreateCheckoutSessionResponse,
  SubscriptionStatusResponse,
  UsageInfoResponse,
  ConnectedAccount,
  ManagedAccount,
  AccountPermanentDeleteResponse,
  AccountVoiceConfig,
  AccountPlatform,
} from './types'
import { ErrorCode } from './types'
import { env } from './env'

const API_BASE_URL = env.apiUrl

// Custom error class for API errors
export class ApiError extends Error {
  constructor(
    public code: string,
    message: string,
    public details?: Record<string, unknown>
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

class ApiClient {
  private baseUrl: string
  private token: string | null = null

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl
    this.token = localStorage.getItem('auth_token')
  }

  setToken(token: string | null) {
    this.token = token
    if (token) {
      localStorage.setItem('auth_token', token)
    } else {
      localStorage.removeItem('auth_token')
    }
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...options.headers,
    }

    if (this.token) {
      (headers as Record<string, string>)['Authorization'] = `Bearer ${this.token}`
    }

    let response: Response
    try {
      response = await fetch(`${this.baseUrl}${endpoint}`, {
        ...options,
        headers,
      })
    } catch (fetchError) {
      // Network error (server down, CORS, etc.)
      const error = new ApiError(
        'NETWORK_ERROR',
        fetchError instanceof Error ? fetchError.message : 'Network request failed',
        { endpoint }
      )
      Sentry.captureException(error, {
        tags: { api_endpoint: endpoint, error_type: 'fetch_failed' },
        extra: { originalError: String(fetchError) },
      })
      throw error
    }

    let data: unknown
    try {
      data = await response.json()
    } catch {
      // Non-JSON response (e.g., HTML error page from proxy/gateway)
      const error = new ApiError(
        'NETWORK_ERROR',
        `Request failed with status ${response.status}`,
        { status: response.status }
      )
      Sentry.captureException(error, {
        tags: { api_endpoint: endpoint, error_type: 'network' },
        extra: { status: response.status },
      })
      throw error
    }

    if (!response.ok) {
      const errorResponse = data as ApiErrorResponse
      const error = new ApiError(
        errorResponse.error?.code || 'UNKNOWN_ERROR',
        errorResponse.error?.message || 'An error occurred',
        errorResponse.error?.details
      )
      Sentry.captureException(error, {
        tags: { api_endpoint: endpoint, error_code: error.code },
        extra: { status: response.status, details: error.details },
      })
      throw error
    }

    return data as T
  }

  // ============================================
  // Authentication
  // ============================================

  async register(email: string, password: string): Promise<RegisterResponse> {
    return this.request<RegisterResponse>('/auth/register', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    })
  }

  async login(email: string, password: string): Promise<LoginResponse> {
    const response = await this.request<LoginResponse>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    })
    if (response.token) {
      this.setToken(response.token)
    }
    return response
  }

  async logout(): Promise<void> {
    await this.request<{ success: true }>('/auth/logout', { method: 'POST' })
    this.setToken(null)
  }

  async getCurrentUser(): Promise<UserPublic> {
    const response = await this.request<{ success: true; user: UserPublic }>('/auth/me')
    return response.user
  }

  // ============================================
  // Password Reset
  // ============================================

  async requestPasswordReset(email: string): Promise<PasswordResetRequestResponse> {
    return this.request<PasswordResetRequestResponse>('/auth/password-reset/request', {
      method: 'POST',
      body: JSON.stringify({ email }),
    })
  }

  async verifyPasswordResetToken(token: string): Promise<PasswordResetVerifyResponse> {
    return this.request<PasswordResetVerifyResponse>(
      `/auth/password-reset/verify?token=${encodeURIComponent(token)}`
    )
  }

  async completePasswordReset(token: string, newPassword: string): Promise<PasswordResetCompleteResponse> {
    return this.request<PasswordResetCompleteResponse>('/auth/password-reset/complete', {
      method: 'POST',
      body: JSON.stringify({ token, newPassword }),
    })
  }

  async changePassword(currentPassword: string, newPassword: string): Promise<PasswordChangeResponse> {
    return this.request<PasswordChangeResponse>('/auth/password-change', {
      method: 'POST',
      body: JSON.stringify({ currentPassword, newPassword }),
    })
  }

  // ============================================
  // Platform OAuth
  // ============================================

  async startPlatformOAuth(platform: AccountPlatform, options?: { handle?: string }): Promise<string> {
    const params = new URLSearchParams()
    if (options?.handle) params.set('handle', options.handle)
    const query = params.toString()
    const response = await this.request<{ success: true; auth_url: string }>(`/auth/${platform}/connect${query ? `?${query}` : ''}`)
    return response.auth_url
  }

  /** @deprecated Use startPlatformOAuth('twitter') */
  async startTwitterOAuth(): Promise<string> {
    return this.startPlatformOAuth('twitter')
  }

  async getTwitterAccount(): Promise<TwitterAccountPublic | null> {
    try {
      // Backend returns twitter_account with created_at, we normalize to connected_at
      const response = await this.request<{
        success: true
        twitter_account: {
          twitter_user_id: string
          username: string
          display_name?: string
          profile_image_url?: string
          created_at: string
        }
      }>('/auth/twitter/account')
      const account = response.twitter_account
      return {
        twitter_user_id: account.twitter_user_id,
        username: account.username,
        display_name: account.display_name,
        profile_image_url: account.profile_image_url,
        connected_at: account.created_at,
      }
    } catch (err) {
      if (err instanceof ApiError && err.code === ErrorCode.TWITTER_NOT_CONNECTED) {
        return null
      }
      throw err
    }
  }

  async disconnectTwitter(): Promise<{ success: boolean }> {
    return this.request<{ success: boolean }>('/auth/twitter/disconnect', {
      method: 'DELETE',
    })
  }

  // ============================================
  // Accounts
  // ============================================

  async getAccounts(): Promise<{ success: true; accounts: ConnectedAccount[] }> {
    return this.request<{ success: true; accounts: ConnectedAccount[] }>('/accounts')
  }

  async getManagedAccounts(): Promise<{ success: true; accounts: ManagedAccount[] }> {
    return this.request<{ success: true; accounts: ManagedAccount[] }>('/accounts?include_disconnected=true')
  }

  async reconnectAccount(id: number): Promise<string> {
    const response = await this.request<{ success: true; auth_url: string }>(`/accounts/${id}/reconnect`, {
      method: 'POST',
      body: JSON.stringify({}),
    })
    return response.auth_url
  }

  async disconnectAccount(id: number): Promise<{ success: true }> {
    return this.request<{ success: true }>(`/accounts/${id}`, { method: 'DELETE' })
  }

  async deleteAccountPermanently(id: number): Promise<AccountPermanentDeleteResponse> {
    return this.request<AccountPermanentDeleteResponse>(`/accounts/${id}/permanent`, {
      method: 'DELETE',
    })
  }

  async analyzeAccountVoice(accountId: number): Promise<VoiceAnalysisResponse> {
    return this.request<VoiceAnalysisResponse>(`/accounts/${accountId}/voice/analyze`, {
      method: 'POST',
      body: JSON.stringify({}),
    })
  }

  async getAccountVoiceProfile(accountId: number): Promise<VoiceProfileResponse> {
    return this.request<VoiceProfileResponse>(`/accounts/${accountId}/voice/profile`)
  }

  async getAccountVoiceAnalysisStatus(accountId: number, jobId: number): Promise<VoiceAnalysisStatusResponse> {
    return this.request<VoiceAnalysisStatusResponse>(`/accounts/${accountId}/voice/status/${jobId}`)
  }

  async updateAccountVoiceConfig(
    accountId: number,
    config: Partial<AccountVoiceConfig>
  ): Promise<{ success: true; voice_config: AccountVoiceConfig }> {
    return this.request<{ success: true; voice_config: AccountVoiceConfig }>(
      `/accounts/${accountId}/voice/config`,
      { method: 'PUT', body: JSON.stringify(config) }
    )
  }

  // ============================================
  // Voice Analysis
  // ============================================

  /** @deprecated Use analyzeAccountVoice instead */
  async analyzeVoice(): Promise<VoiceAnalysisResponse> {
    return this.request<VoiceAnalysisResponse>('/voice/analyze', {
      method: 'POST',
      body: JSON.stringify({}),
    })
  }

  /** @deprecated Use getAccountVoiceAnalysisStatus instead */
  async getVoiceAnalysisStatus(jobId: number): Promise<VoiceAnalysisStatusResponse> {
    return this.request<VoiceAnalysisStatusResponse>(`/voice/status/${jobId}`)
  }

  /** @deprecated Use getAccountVoiceProfile instead */
  async getVoiceProfile(): Promise<VoiceProfileResponse> {
    return this.request<VoiceProfileResponse>('/voice/profile')
  }

  // ============================================
  // Content Generation & Queue
  // ============================================

  async generateContent(
    prompt: string,
    count: number = 1,
    options?: {
      useVoice?: boolean
      contentType?: ContentType
      toneOverride?: ToneOverride
      context?: { type: ContextType; value: string }
      accountId?: number
    }
  ): Promise<ContentGenerateResponse> {
    return this.request<ContentGenerateResponse>('/content/generate', {
      method: 'POST',
      body: JSON.stringify({
        prompt,
        count,
        use_voice: options?.useVoice,
        content_type: options?.contentType,
        tone_override: options?.toneOverride,
        context: options?.context,
        account_id: options?.accountId,
      }),
    })
  }

  async getGenerationJobStatus(jobId: number): Promise<GenerationJobStatusResponse> {
    return this.request<GenerationJobStatusResponse>(`/content/jobs/${jobId}`)
  }

  async getQueue(accountId?: number): Promise<QueueListResponse> {
    const query = accountId != null ? `?account_id=${accountId}` : ''
    return this.request<QueueListResponse>(`/content/queue${query}`)
  }

  async getQueueItem(id: number): Promise<QueueItemResponse> {
    return this.request<QueueItemResponse>(`/content/queue/${id}`)
  }

  async updateQueueItem(id: number, data: QueueItemUpdateRequest): Promise<QueueItemUpdateResponse> {
    return this.request<QueueItemUpdateResponse>(`/content/queue/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  }

  async deleteQueueItem(id: number): Promise<void> {
    await this.request<{ success: true }>(`/content/queue/${id}`, { method: 'DELETE' })
  }

  async postToPlatform(id: number): Promise<PostToPlatformResponse> {
    return this.request<PostToPlatformResponse>(`/content/post/${id}`, {
      method: 'POST',
      body: JSON.stringify({}),
    })
  }

  /** @deprecated Use postToPlatform */
  async postToTwitter(id: number): Promise<PostToTwitterResponse> {
    return this.request<PostToTwitterResponse>(`/content/post/${id}`, {
      method: 'POST',
      body: JSON.stringify({}),
    })
  }

  async uploadMedia(file: File): Promise<MediaUploadResponse> {
    const formData = new FormData()
    formData.append('file', file)

    const headers: HeadersInit = {}
    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`
    }

    let response: Response
    try {
      response = await fetch(`${this.baseUrl}/media/upload`, {
        method: 'POST',
        headers,
        body: formData,
      })
    } catch (fetchError) {
      throw new ApiError(
        'NETWORK_ERROR',
        fetchError instanceof Error ? fetchError.message : 'Network request failed',
        { endpoint: '/media/upload' }
      )
    }

    let data: unknown
    try {
      data = await response.json()
    } catch {
      throw new ApiError(
        'NETWORK_ERROR',
        `Upload failed with status ${response.status}`,
        { status: response.status }
      )
    }

    if (!response.ok) {
      const errorResponse = data as ApiErrorResponse
      throw new ApiError(
        errorResponse.error?.code || 'MEDIA_UPLOAD_FAILED',
        errorResponse.error?.message || 'Failed to upload media',
        errorResponse.error?.details
      )
    }

    return data as MediaUploadResponse
  }

  async scheduleContent(id: number, scheduledFor: string): Promise<ScheduleContentResponse> {
    return this.request<ScheduleContentResponse>(`/content/schedule/${id}`, {
      method: 'POST',
      body: JSON.stringify({ scheduled_for: scheduledFor }),
    })
  }

  async cancelSchedule(id: number): Promise<CancelScheduleResponse> {
    return this.request<CancelScheduleResponse>(`/content/schedule/${id}/cancel`, {
      method: 'POST',
      body: JSON.stringify({}),
    })
  }

  async remixContent(
    id: number,
    overrides?: { prompt?: string; tone_override?: ToneOverride | null }
  ): Promise<RemixResponse> {
    return this.request<RemixResponse>(`/content/remix/${id}`, {
      method: 'POST',
      body: JSON.stringify(overrides ?? {}),
    })
  }

  // ============================================
  // Analytics
  // ============================================

  async getAnalytics(): Promise<AnalyticsListResponse> {
    return this.request<AnalyticsListResponse>('/analytics/posts')
  }

  async getAnalyticsSummary(): Promise<AnalyticsSummaryResponse> {
    return this.request<AnalyticsSummaryResponse>('/analytics/summary')
  }

  async refreshMetrics(id: number): Promise<MetricsRefreshResponse> {
    return this.request<MetricsRefreshResponse>(`/analytics/refresh/${id}`, {
      method: 'POST',
      body: JSON.stringify({}),
    })
  }

  // ============================================
  // Settings
  // ============================================

  async getSettings(): Promise<SettingsGetResponse> {
    return this.request<SettingsGetResponse>('/settings')
  }

  async updateSettings(data: SettingsUpdateRequest): Promise<SettingsUpdateResponse> {
    return this.request<SettingsUpdateResponse>('/settings', {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  }

  // ============================================
  // Onboarding
  // ============================================

  async getOnboardingProgress(): Promise<OnboardingProgressResponse> {
    return this.request<OnboardingProgressResponse>('/onboarding/progress')
  }

  async completeOnboarding(): Promise<{ success: true }> {
    return this.request<{ success: true }>('/onboarding/complete', {
      method: 'POST',
      body: JSON.stringify({}),
    })
  }

  // ============================================
  // Billing
  // ============================================

  async createCheckoutSession(
    plan: PlanTier,
    successUrl: string,
    cancelUrl: string
  ): Promise<CreateCheckoutSessionResponse> {
    return this.request<CreateCheckoutSessionResponse>('/billing/checkout', {
      method: 'POST',
      body: JSON.stringify({ plan, success_url: successUrl, cancel_url: cancelUrl }),
    })
  }

  async getSubscriptionStatus(): Promise<SubscriptionStatusResponse> {
    return this.request<SubscriptionStatusResponse>('/billing/subscription')
  }

  async getUsage(): Promise<UsageInfoResponse> {
    return this.request<UsageInfoResponse>('/billing/usage')
  }
}

export const api = new ApiClient(API_BASE_URL)
